import { useState } from 'react'
import { useAuthStore } from '../../store/authStore'
import { useNavigate, Link } from 'react-router-dom'
import {
  hasAccess,
  userPlan,
  PLAN_LABELS,
  PLAN_COLORS,
  PLAN_BG_COLORS,
  TIERS,
  type Plan,
} from '../../lib/subscriptionTiers'

interface TierGateProps {
  /** Human-readable feature name shown in the lock overlay. */
  feature: string
  /** Minimum plan required. Default: 'pro'. */
  minTier?: Plan
  children: React.ReactNode
}

const STORAGE_KEY = 'bb_pro_unlocked'

/**
 * Wraps features that require a minimum subscription tier.
 * Renders a lock overlay for users below the required plan.
 *
 * Tier hierarchy: free < pro < pro_plus < team
 *
 * @example
 * <TierGate feature="Supply Chain Intelligence" minTier="pro_plus">
 *   <SupplyChainPage />
 * </TierGate>
 */
export function TierGate({ feature, minTier = 'pro', children }: TierGateProps) {
  const { user, loading } = useAuthStore()
  const navigate = useNavigate()
  const [showPasscode, setShowPasscode] = useState(false)
  const [passcode, setPasscode]         = useState('')
  const [error, setError]               = useState(false)
  const [unlocked, setUnlocked]         = useState(() => localStorage.getItem(STORAGE_KEY) === '1')

  if (loading) return null

  const isUnlocked = unlocked || hasAccess(user, minTier)
  if (isUnlocked) return <div className="h-full w-full">{children}</div>

  const tier = TIERS.find((t) => t.id === minTier)!
  const currentPlan = userPlan(user)
  const tierLabel   = PLAN_LABELS[minTier]
  const tierColor   = PLAN_COLORS[minTier]
  const tierBg      = PLAN_BG_COLORS[minTier]

  const upgradeLabel =
    !user ? 'Sign In / Create Account'
    : currentPlan === 'free' ? `Upgrade to ${tierLabel}`
    : `Upgrade to ${tierLabel}`

  const upgradeAction = !user
    ? () => navigate('/login')
    : () => navigate('/settings?tab=billing')

  const highlightFeatures = tier.features.filter((f) => f.included).slice(0, 4)

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Blurred preview */}
      <div className="pointer-events-none select-none opacity-20 blur-sm h-full">
        {children}
      </div>

      {/* Lock overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-bb-bg/85 backdrop-blur-sm z-10">
        <div className="bb-panel p-8 max-w-sm w-full mx-4 text-center space-y-4 border border-bb-border">

          {/* Tier badge */}
          <div className="flex flex-col items-center gap-2">
            <div className="text-3xl">🔒</div>
            <span className={`text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${tierColor} ${tierBg}`}>
              {tierLabel} Feature
            </span>
          </div>

          <h2 className="text-bb-text text-base font-semibold">{feature}</h2>
          <p className="text-bb-subtle text-xs leading-relaxed">{tier.description}</p>

          {/* Feature highlights */}
          <div className="grid grid-cols-2 gap-1.5 text-left pt-1">
            {highlightFeatures.map((f) => (
              <div key={f.label} className="flex items-start gap-1.5">
                <span className={`mt-0.5 text-xs shrink-0 ${tierColor}`}>✓</span>
                <span className="text-2xs text-bb-subtle leading-snug">{f.label}</span>
              </div>
            ))}
          </div>

          {/* Price callout */}
          <div className="bg-bb-surface rounded border border-bb-border/50 py-2.5 px-3 text-center">
            <span className={`text-xl font-bold ${tierColor}`}>{tier.price}</span>
            <span className="text-bb-disabled text-xs">{tier.period}</span>
            {tier.annualPrice && (
              <div className="text-2xs text-bb-green mt-0.5">
                {tier.annualPrice}/mo billed annually
              </div>
            )}
            <div className="text-2xs text-bb-disabled mt-0.5">{tier.targetUser}</div>
          </div>

          {/* CTAs */}
          <div className="space-y-2 pt-1">
            {minTier === 'team' ? (
              <a
                href="mailto:sales@quanttradeai.com"
                className="block bb-btn-primary w-full text-sm text-center"
              >
                Contact Sales
              </a>
            ) : (
              <button className="bb-btn-primary w-full text-sm" onClick={upgradeAction}>
                {upgradeLabel}
              </button>
            )}

            {user && (
              <Link
                to="/settings?tab=billing"
                className="block text-2xs text-bb-disabled hover:text-bb-subtle transition-colors"
              >
                Compare all plans →
              </Link>
            )}

            {/* Founder passcode */}
            {!showPasscode ? (
              <button
                className="w-full text-2xs text-bb-disabled hover:text-bb-subtle transition-colors py-1"
                onClick={() => setShowPasscode(true)}
              >
                Enter passcode
              </button>
            ) : (
              <div className="flex gap-2">
                <input
                  autoFocus
                  type="password"
                  className="bb-input flex-1 text-xs"
                  placeholder="Passcode"
                  value={passcode}
                  onChange={(e) => { setPasscode(e.target.value); setError(false) }}
                  onKeyDown={(e) => e.key === 'Enter' && handlePasscode()}
                />
                <button className="bb-btn-ghost text-xs shrink-0" onClick={handlePasscode}>
                  Unlock
                </button>
              </div>
            )}
            {error && <p className="text-2xs text-bb-red text-center">Incorrect passcode</p>}
          </div>
        </div>
      </div>
    </div>
  )

  function handlePasscode() {
    const expected = import.meta.env.VITE_FOUNDER_PASSCODE as string | undefined
    if (expected && passcode === expected) {
      localStorage.setItem(STORAGE_KEY, '1')
      setUnlocked(true)
    } else {
      setError(true)
      setPasscode('')
    }
  }
}
